import api from '../api';
import {ElMessage} from "element-plus";

const BRANDING_CACHE_KEY = 'kodi_branding_cache';

// Read synchronously as this module loads - before Vuex finishes setting
// up the store, let alone before Vue mounts anything - so the very first
// render of Sidebar.vue/App.vue already has the business's real colors
// instead of starting from null and visibly switching once getMyBranding()'s
// API call resolves. cacheBranding() below keeps this in sync on every
// confirmed (not just previewed) branding change.
function readCachedBranding() {
    try {
        const raw = localStorage.getItem(BRANDING_CACHE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
}

function cacheBranding(branding) {
    try {
        if (branding) {
            localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(branding));
        } else {
            localStorage.removeItem(BRANDING_CACHE_KEY);
        }
    } catch (e) {
        // Best-effort only - worst case is the flash-prevention above
        // doesn't kick in on the next load, not a functional break.
    }
}

export default {
    namespaced: true,
    state: {
        loading: false,
        uploadingLogo: false,
        business: null,
        has_errors: false,
        errors: {data: {errors: {}}},
        branding: readCachedBranding(),
    },
    getters: {
        loading: state => state.loading,
        uploadingLogo: state => state.uploadingLogo,
        business: state => state.business,
        has_errors: state => state.has_errors,
        errors: state => state.errors,
        branding: state => state.branding,
    },
    mutations: {
        setLoading(state, value) {
            state.loading = value;
        },
        setUploadingLogo(state, value) {
            state.uploadingLogo = value;
        },
        // Keeps `branding` (what Sidebar.vue actually reads, on every page,
        // for every staff login) in sync whenever the full business record
        // changes here - getBusinessSettings/updateBusinessSettings/
        // uploadBusinessLogo all funnel through this one mutation. Without
        // this, changing or resetting the sidebar color on Profile.vue only
        // took effect on the *next* full navigation (whenever Sidebar
        // happened to remount and re-fetch on its own) rather than
        // immediately, since Sidebar reads a separate piece of state that
        // nothing was updating.
        setBusiness(state, business) {
            state.business = business;
            if (business) {
                state.branding = {
                    logo_url: business.logo_url,
                    sidebar_color: business.sidebar_color,
                    sidebar_text_color: business.sidebar_text_color,
                    brand_color: business.brand_color,
                };
                cacheBranding(state.branding);
            }
        },
        hasErrors(state, value) {
            state.has_errors = value;
        },
        setErrors(state, errors) {
            state.errors = errors;
        },
        setBranding(state, branding) {
            state.branding = branding;
        },
    },
    actions: {
        async getBusinessSettings({commit}) {
            commit('setLoading', true);
            try {
                const response = await api.getBusinessSettings();
                commit('setBusiness', response.data);
            } catch (e) {
                ElMessage.error(e.response?.data?.message || 'Error loading business details.');
            } finally {
                commit('setLoading', false);
            }
        },
        async updateBusinessSettings({commit}, data) {
            commit('setLoading', true);
            commit('hasErrors', false);
            try {
                const response = await api.updateBusinessSettings(data);
                commit('setBusiness', response.data);
                ElMessage.success('Business details updated successfully.');
            } catch (e) {
                commit('hasErrors', true);
                commit('setErrors', e.response);
                ElMessage.error(e.response?.data?.message || 'Error updating business details.');
                throw e;
            } finally {
                commit('setLoading', false);
            }
        },
        // Local-only, no API call - lets Profile.vue reflect the color
        // picker's in-progress value (fired continuously while dragging
        // inside the picker, before the user has confirmed anything) onto
        // the live sidebar instantly, instead of waiting on a round trip
        // per drag movement - or worse, firing an actual save request for
        // every intermediate value while dragging. handleSidebarColorChange
        // (bound to the picker's `change` event, which only fires once a
        // value is actually confirmed) is what persists it for real.
        previewSidebarColor({commit, state}, color) {
            commit('setBranding', {...state.branding, sidebar_color: color});
        },
        // Same reasoning as previewSidebarColor - kept as its own action
        // rather than a generic "previewField" one, matching this store's
        // existing style of one explicit action per concern.
        previewSidebarTextColor({commit, state}, color) {
            commit('setBranding', {...state.branding, sidebar_text_color: color});
        },
        // Same reasoning as previewSidebarColor - App.vue watches
        // branding.brand_color and applies it globally (see
        // utils/brandTheme.js) as this fires, so the live preview covers
        // buttons/links everywhere, not just the sidebar.
        previewBrandColor({commit, state}, color) {
            commit('setBranding', {...state.branding, brand_color: color});
        },
        // No error toast here on purpose - this runs unconditionally on
        // every page load (see Sidebar.vue) for every user, including ones
        // with no business yet; a failure here should just fall back to
        // the default Kodi look silently, not interrupt anything.
        async getMyBranding({commit}) {
            try {
                const response = await api.getMyBranding();
                commit('setBranding', response.data);
                cacheBranding(response.data);
            } catch (e) {
                commit('setBranding', null);
                cacheBranding(null);
            }
        },
        async uploadBusinessLogo({commit}, file) {
            commit('setUploadingLogo', true);
            try {
                const response = await api.uploadBusinessLogo(file);
                commit('setBusiness', response.data);
                ElMessage.success('Business logo updated successfully.');
            } catch (e) {
                ElMessage.error(e.response?.data?.message || 'Error uploading business logo.');
                throw e;
            } finally {
                commit('setUploadingLogo', false);
            }
        },
    },
}
